import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { puedeUsarCanal } from '@shared/permisos';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Tags,
  Users,
  Settings as SettingsIcon,
  ChefHat,
  Table2,
  Combine,
  Receipt,
  BarChart3,
  TrendingUp,
  Wallet,
  Boxes,
  Tag,
  Bike,
  Lock,
  ChevronDown,
  ChevronUp,
  ListChecks,
  BookOpen,
} from 'lucide-react';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /**
   * Sprint 63 (Fase 5) — Canal representativo de la pantalla.
   *
   * Antes cada item llevaba una lista de roles escrita a mano, que había que mantener
   * sincronizada con la autorización real del servidor — y cuando se desincroniza, el
   * operador ve un botón que lo lleva a un error. Ahora se pregunta a la MISMA matriz que
   * autoriza en el servidor: si no puede usar el canal, no ve el botón.
   *
   * Esto es sólo UX. La autoridad sigue siendo `safeHandleConPermiso`: esconder un botón
   * no es control de acceso.
   */
  canal: string;
  /** Sprint 114 — Qué contador se pinta en rojo, si hay algo esperando. */
  contador?: 'pedidos' | 'cocina';
}

/**
 * Sprint 114 — El menú, ordenado por CUÁNDO se usa.
 *
 * ## De dónde sale
 *
 * El dueño, sobre la operadora que está aprendiendo el sistema: *"en tecnología del 1 al 10 tiene
 * 3 o 4 máximo"*, y sobre su jornada: *"pues abro la caja, registro existencia, y todo el día son
 * atender los pedidos **por donde lleguen**; fin del día cierro caja"*.
 *
 * El menú tenía **19 destinos, 12 visibles**, contra la regla del propio proyecto de 5–7 (Hick).
 * No estaba desordenado: estaba **sin jerarquía**, que es peor. Un menú de 19 no se lee, se
 * memoriza por posición — y eso aguanta hasta que alguien agrega una entrada.
 *
 * ## Los tres bloques
 *
 * No agrupan por tema sino por **cada cuánto se tocan**, que es lo que el operador vive:
 *   - **Mi día** — lo de cien veces al día.
 *   - **Abrir y cerrar** — lo de una vez al día, en sus dos extremos.
 *   - **Mi negocio** — lo de una vez al mes, o cuando algo cambia.
 *
 * Aunque no se lea el encabezado, la posición ya enseña. **No se quitó ningún destino.**
 *
 * ## Lo que se arregló de paso
 *
 * **El Corte de Caja estaba dentro de «Avanzado» y «Cerrar Día» fuera** — el mismo momento de la
 * jornada partido en dos niveles, con uno detrás de una palabra que a alguien de 3/10 le dice
 * *"esto no es para ti"*. Ahí hay un fracaso documentado: el 2026-05-23, ocho horas sin cerrar.
 *
 * ## Por qué AHORA y no después
 *
 * **Todavía no tiene ventas reales: está aprendiendo.** No hay memoria muscular que romper, así
 * que reordenar hoy cuesta cero. Dentro de tres meses, con las 19 entradas ya memorizadas por
 * posición, el mismo cambio costaría semanas de reaprendizaje.
 */
interface Grupo {
  titulo: string;
  items: NavItem[];
}

const GRUPOS: Grupo[] = [
  {
    titulo: 'Mi día',
    items: [
      // `Inicio` no es una sola pantalla: el tablero se adapta al oficio (DashboardMesero
      // existe desde el Sprint 31). Por eso el canal es uno que todos tienen; a cocina la
      // manda a su cola el propio Dashboard.
      { to: '/dashboard', label: 'Inicio', icon: LayoutDashboard, canal: 'config:obtener' },
      /**
       * Sprint 123 — Una sola entrada para los pedidos, lleguen por donde lleguen.
       *
       * Antes había tres —`Pedidos`, `Mostrador`, `A domicilio`— para responder una sola pregunta:
       * *¿qué me falta?*. El dueño lo dijo describiendo el día de quien atiende sola: *"todo el día
       * son atender los pedidos **por donde lleguen**"*, y el sistema la obligaba a lo contrario:
       * elegir el canal antes de poder atender.
       *
       * **`Mostrador` se retira aquí** porque sus tres acciones —surtir, cobrar, entregar— ya viven
       * en la bandeja. Su ruta sigue respondiendo un sprint más, por si algo enlaza a ella.
       *
       * **`A domicilio` NO se retira todavía**, y se dice en voz alta: sus modales —cotizar el
       * envío, la espera del comprobante con su prórroga, asignar la moto, las devoluciones
       * pendientes— aún viven allí, y la bandeja navega a ese tablero para resolverlos. Retirarlo
       * ahora dejaría acciones sin puerta, que es exactamente lo que este rediseño vino a evitar.
       */
      { to: '/bandeja', label: 'Pedidos', icon: Receipt, canal: 'pedidos:bandeja', contador: 'pedidos' },
      { to: '/mesas', label: 'Mesas', icon: Table2, canal: 'mesas:listar' },
      /**
       * Sprint 76 — "A domicilio" (no "Delivery": en el piso nadie dice delivery).
       * Sprint 99 — Y ahora es **la** pantalla del domicilio, no una de tres.
       */
      { to: '/domicilios', label: 'A domicilio', icon: Bike, canal: 'pedidos:tablero-domicilios' },
      { to: '/cocina', label: 'Cocina', icon: ChefHat, canal: 'pedidos:marcar-listo', contador: 'cocina' },
      /**
       * Sprint 145 — "Ventas del día", con ese nombre y a la vista.
       *
       * La dueña lo pidió mientras vendía: se equivocó capturando, quiso corregir y **no encontró
       * dónde**. La lista existía detrás de un desplegable "Activos / Todos" en otra pantalla, que
       * es tanto como no existir cuando hay un cliente esperando.
       *
       * Va aquí, en "Mi día", porque es parte de la jornada y no de los reportes: se entra a
       * arreglar algo que acaba de pasar, no a analizar el mes.
       */
      /*
        Sprint 147 — Se llamaba "Ventas del día" y desde este sprint se pueden pedir otros días:
        el nombre del menú tenía que dejar de prometer uno solo. "Ventas" sigue siendo lo que ella
        fue a buscar cuando preguntó dónde estaban las suyas.
      */
      { to: '/ventas', label: 'Ventas', icon: Receipt, canal: 'pedidos:ventas-del-dia' },
    ],
  },
  {
    titulo: 'Abrir y cerrar',
    items: [
      /**
       * Sprint 114 — El Corte de Caja sube desde «Avanzado».
       *
       * Abrir la caja es **lo primero que hace en el día** (*"pues abro la caja…"*) y cerrarla es
       * lo último. Estaba a dos clics detrás de un desplegable llamado "Avanzado", mientras
       * "Cerrar Día" —su otra mitad— vivía arriba y a la vista.
       */
      { to: '/corte-caja', label: 'Corte de Caja', icon: Wallet, canal: 'corte:abrir' },
      // Sprint 110 — "Existencias" dice lo que se hace aquí; "Inventario" sonaba a un catálogo
      // aparte del menú, y por eso se usó como tal: para dar de alta y para borrar.
      // Sprint 114 — Y ahora es el único nombre: se acabaron los tres vocabularios.
      { to: '/inventario', label: 'Existencias', icon: Boxes, canal: 'insumos:listar' },
      { to: '/cerrar-dia', label: 'Cerrar Día', icon: Lock, canal: 'cierre-dia:preview' },
    ],
  },
  {
    titulo: 'Mi negocio',
    items: [
      // Sprint 170 — Las cuatro preguntas de la dueña y qué hacer con ellas. Sólo dueño (sección
      // `estadisticas`, fail-closed): la caja ve Reportes, no la estrategia del negocio.
      { to: '/estadisticas', label: 'Cómo va el negocio', icon: TrendingUp, canal: 'estadisticas:tablero' },
      { to: '/reportes', label: 'Reportes', icon: BarChart3, canal: 'reportes:cancelados' },
      { to: '/usuarios', label: 'Usuarios', icon: Users, canal: 'usuarios:crear' },
      { to: '/settings', label: 'Configuración', icon: SettingsIcon, canal: 'config:actualizar' },
    ],
  },
];

/**
 * Sprint 64 — El menú del negocio, agrupado.
 *
 * Reporte del negocio: *"me quedé pensando si vale el esfuerzo reorganizar o agrupar…
 * Inventario, Categorías, Productos, Paquetes, Promociones, para que sea UN SOLO MÉTODO
 * DE ENTRADA y siempre sepa el orden del proceso"*.
 *
 * Son los cuatro que se tocan al arrancar y luego casi nunca. **Existencias sigue fuera a
 * propósito**: cargar la carne del día es la operación más repetida del negocio, y esconderla
 * tras un clic sería castigar la tarea diaria por ordenar la que casi no se usa.
 */
const menuCatalogo: NavItem[] = [
  { to: '/categorias', label: 'Categorías', icon: Tags, canal: 'categorias:crear' },
  { to: '/productos', label: 'Productos', icon: Package, canal: 'productos:crear' },
  { to: '/paquetes', label: 'Paquetes', icon: Combine, canal: 'paquetes:crear' },
  { to: '/promos', label: 'Promociones', icon: Tag, canal: 'promociones:crear' },
];

const primerosPasosItem: NavItem = {
  to: '/primeros-pasos',
  label: 'Primeros pasos',
  icon: ListChecks,
  canal: 'productos:crear',
};

export function Sidebar() {
  const sesion = useSessionStore((s) => s.sesion);
  const [menuAbierto, setMenuAbierto] = useState(false);
  /**
   * Sprint 64 — ¿Hay algo que vender?
   *
   * Mientras el catálogo esté vacío, "Primeros pasos" va **arriba y resaltado**: el
   * reporte fue *"está a la mitad del menú, escondido, sin que diga HEY MÍRAME SOY
   * IMPORTANTE"*. Cuando ya hay productos deja de gritar.
   */
  const [hayProductos, setHayProductos] = useState<boolean | null>(null);
  /**
   * Sprint 114 — El menú deja de ser una lista y dice dónde hay algo esperando.
   *
   * Un contador en rojo le ahorra entrar a mirar. Sale de `cierreDia.pendientes()`, que ya
   * calcula exactamente esto para el cierre —pedidos vivos por área— así que no se agrega
   * ninguna consulta nueva al modelo.
   *
   * Si el oficio no puede usar ese canal, **no hay contador y no se llama**: un badge no es
   * información que se le deba a todo el mundo, y pedirlo sin permiso sería un 403 cada 30 s.
   */
  const [pendientes, setPendientes] = useState<{ total: number; cocina: number } | null>(null);

  const puedeVerContadores = !!sesion && puedeUsarCanal(sesion.rol, 'cierre-dia:preview');

  useEffect(() => {
    if (!sesion || !puedeUsarCanal(sesion.rol, 'productos:crear')) return;
    let vivo = true;
    void window.api.productos.listar().then((res) => {
      if (vivo && res.ok) setHayProductos(res.data.length > 0);
    });
    return () => {
      vivo = false;
    };
  }, [sesion]);

  useEffect(() => {
    if (!puedeVerContadores) return;
    let vivo = true;
    const leer = () =>
      void window.api.cierreDia.pendientes().then((r) => {
        /*
          Sprint 164 — El "Pedidos 7" que llevaba a "Nada pendiente": contaba pedidos de días pasados que
          la pantalla esconde a propósito. Ésos los pregunta la tarjeta "sin concluir" al abrir el día;
          el número del menú sólo cuenta lo que se encuentra al entrar.
        */
        if (vivo && r.ok) setPendientes({ total: Math.max(0, r.data.total - (r.data.sinConcluir ?? 0)), cocina: r.data.cocina.length });
      });
    leer();
    const t = setInterval(leer, 30_000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [puedeVerContadores]);

  if (!sesion) return null;

  const subMenu = menuCatalogo.filter((i) => puedeUsarCanal(sesion.rol, i.canal));
  const verPrimerosPasos = puedeUsarCanal(sesion.rol, primerosPasosItem.canal);
  const catalogoVacio = hayProductos === false;

  const cuentaDe = (item: NavItem): number => {
    if (!item.contador || !pendientes) return 0;
    return item.contador === 'cocina' ? pendientes.cocina : pendientes.total;
  };

  const renderItem = (item: NavItem) => {
    const n = cuentaDe(item);
    return (
      <li key={item.to}>
        <NavLink
          to={item.to}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? 'bg-primary text-primary-fg' : 'text-slate-700 hover:bg-slate-100'
            }`
          }
        >
          {({ isActive }: { isActive: boolean }) => (
            <>
              <item.icon className="h-5 w-5" />
              <span className="flex-1">{item.label}</span>
              {n > 0 && (
                <span
                  /* Rojo sólido: es lo único del menú que pide atención, así que no compite con nada. */
                  className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                    isActive ? 'bg-white text-primary' : 'bg-red-600 text-white'
                  }`}
                  aria-label={`${n} pendiente${n === 1 ? '' : 's'}`}
                >
                  {n}
                </span>
              )}
            </>
          )}
        </NavLink>
      </li>
    );
  };

  return (
    <nav className="flex w-48 shrink-0 flex-col border-r border-slate-200 bg-white overflow-y-auto xl:w-56">
      <ul className="flex-1 space-y-1 p-3">
        {/* Sprint 64 — Con el catálogo vacío, la guía es lo primero y se ve distinta. */}
        {verPrimerosPasos && catalogoVacio && (
          <li>
            <NavLink
              to={primerosPasosItem.to}
              className={({ isActive }) =>
                `mb-2 flex items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-colors ${
                  isActive
                    ? 'border-primary bg-primary text-primary-fg'
                    : 'border-primary/40 bg-primary/5 text-primary hover:bg-primary/10'
                }`
              }
            >
              <primerosPasosItem.icon className="h-5 w-5" />
              <span className="flex-1">Empieza aquí</span>
              <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-fg">
                !
              </span>
            </NavLink>
          </li>
        )}

        {GRUPOS.map((grupo, idx) => {
          const visibles = grupo.items.filter((i) => puedeUsarCanal(sesion.rol, i.canal));
          if (visibles.length === 0) return null;
          return (
            <li key={grupo.titulo}>
              {/*
                El encabezado es una etiqueta, no un botón: **nada de esto se colapsa**.
                Lección del Sprint 67 — al mandar "Primeros pasos" dentro de un grupo colapsado, el
                negocio lo reportó con una foto y un círculo rojo: *"¿qué hace escondido PRIMEROS
                PASOS?"*. Agrupar es dar jerarquía, no esconder.

                Sprint 125 — Y para dar jerarquía primero hay que **verse**. Nacieron en
                `text-[10px] text-slate-400`: 10 px y 2.8:1 de contraste, cuando la regla del
                proyecto (§7) pide 4.5:1. El dueño los señaló con marcador rosa en una captura.

                El mismo error que la alerta del inventario, de otra forma: confiar el mensaje a un
                gris que se desvanece. Ahora 12 px, `slate-600` (7.5:1) y **una línea divisoria**,
                para que el agrupamiento se lea aunque el texto no — nunca una sola señal.
              */}
              <div
                className={`px-3 pb-1.5 text-xs font-bold uppercase tracking-wide text-slate-600 ${
                  idx === 0 ? 'pt-3' : 'mt-3 border-t border-slate-200 pt-3'
                }`}
              >
                {grupo.titulo}
              </div>
              <ul className="space-y-1">{visibles.map(renderItem)}</ul>
            </li>
          );
        })}

        {/* Sprint 64 — El menú del negocio, agrupado: se arma una vez y casi no se toca. */}
        {subMenu.length > 0 && (
          <li>
            <button
              type="button"
              onClick={() => setMenuAbierto((v) => !v)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
            >
              <BookOpen className="h-5 w-5" />
              <span className="flex-1 text-left">Menú del negocio</span>
              {menuAbierto ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
            {menuAbierto && (
              <ul className="ml-3 mt-1 space-y-1 border-l border-slate-200 pl-2">
                {subMenu.map(renderItem)}
              </ul>
            )}
          </li>
        )}

        {/*
          Sprint 67 — La guía va FUERA del grupo del catálogo, siempre a la vista y discreta.

          Sprint 114 — Y deja de llevar color propio. El ámbar del Sprint 71 resolvía un problema
          que ya no existe —que se perdiera entre otras doce entradas— y con el menú agrupado se
          había vuelto lo único que resalta en toda la barra: la entrada que **menos** se usa
          compitiendo por la atención con la que se usa cien veces al día. Mientras no haya nada
          que vender sigue gritando arriba, que es cuando de verdad importa.
        */}
        {verPrimerosPasos && !catalogoVacio && (
          <li className="mt-2 border-t border-slate-200 pt-2">
            <NavLink
              to={primerosPasosItem.to}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? 'bg-primary text-primary-fg' : 'text-slate-500 hover:bg-slate-100'
                }`
              }
            >
              <primerosPasosItem.icon className="h-5 w-5" />
              <span>Primeros pasos</span>
            </NavLink>
          </li>
        )}
      </ul>
    </nav>
  );
}

// `ShoppingCart` mantiene el import legible aunque no se use aún (placeholder).
void ShoppingCart;
